/**
 * Compute ÁHK (Árleg Hlutfallstala Kostnaðar) using IRR with binary search.
 * 
 * @param {Array<{tInYears: number, amount: number}>} cashflows
 *   - tInYears: time from start in years (0 for disbursement, 1/12, 2/12... for monthly payments)
 *   - amount: cashflow for customer (positive = receives money, negative = pays money)
 * @returns {number} Annual ÁHK as decimal (e.g., 0.418 for 41.8%)
 */
export function computeAhk(cashflows) {
  if (!cashflows || cashflows.length < 2) return 0;

  // NPV at a given annual rate
  const npv = (rate) => {
    return cashflows.reduce((sum, cf) => {
      return sum + cf.amount / Math.pow(1 + rate, cf.tInYears);
    }, 0);
  };

  // Binary search for IRR on interval [0, 5] (0% to 500%)
  let lo = 0;
  let hi = 5;
  const maxIter = 200;
  const tolerance = 1e-8;

  // Check if solution exists in range
  const npvLo = npv(lo);
  const npvHi = npv(hi);

  // If NPV at both bounds has same sign, no root in range
  if (npvLo * npvHi > 0) {
    // Return the bound that gives NPV closer to 0
    return Math.abs(npvLo) < Math.abs(npvHi) ? lo : hi;
  }

  for (let i = 0; i < maxIter; i++) {
    const mid = (lo + hi) / 2;
    const npvMid = npv(mid);

    if (Math.abs(npvMid) < tolerance) {
      return mid;
    }

    if (npvMid * npvLo > 0) {
      lo = mid;
    } else {
      hi = mid;
    }
  }

  return (lo + hi) / 2;
}

/**
 * Build cashflows array for a loan and compute ÁHK.
 * 
 * @param {number} loanAmount - Principal amount
 * @param {number} lantokugjald - Origination fee (deducted from disbursement)
 * @param {number} termMonths - Loan term in months
 * @param {number} interestRate - Annual interest rate as percentage (e.g., 17.7)
 * @param {number} greidslus - Payment slip fee per month
 * @returns {{ahk: number, cashflows: Array}} ÁHK as decimal and the cashflows used
 */
export function calculateLoanAhk(loanAmount, lantokugjald, termMonths, interestRate, greidslus = 250) {
  if (!loanAmount || !termMonths || !interestRate || loanAmount <= 0 || termMonths <= 0) {
    return { ahk: 0, cashflows: [] };
  }

  const totalFinanced = loanAmount + lantokugjald;
  const rate = interestRate / 100;
  const monthlyRate = rate / 12;

  // Monthly payment (annuity)
  let monthlyPayment;
  if (monthlyRate > 0) {
    monthlyPayment = totalFinanced * (monthlyRate * Math.pow(1 + monthlyRate, termMonths)) / (Math.pow(1 + monthlyRate, termMonths) - 1);
  } else {
    monthlyPayment = totalFinanced / termMonths;
  }

  // Build cashflows from customer perspective:
  // t=0: customer receives (loanAmount - lantokugjald) → net disbursement is positive
  // The customer borrows loanAmount but pays lantokugjald upfront, so net received = loanAmount
  // Actually: the total financed = loanAmount + lantokugjald, but the customer only receives loanAmount
  const netDisbursement = loanAmount; // What customer actually receives

  const cashflows = [
    { tInYears: 0, amount: netDisbursement } // Customer receives this
  ];

  // Monthly payments: customer pays (negative)
  for (let m = 1; m <= termMonths; m++) {
    cashflows.push({
      tInYears: m / 12,
      amount: -(monthlyPayment + greidslus) // Total monthly payment including fee
    });
  }

  const ahk = computeAhk(cashflows);
  return { ahk, cashflows };
}
